import React from 'react';
import NavPerformancePanel from './NavPerformancePanel';
import { ProductFundamentalsCard } from './ProductProfileCard';
import NavAnomalyPanel from './NavAnomalyPanel';
import PerformanceTrendStrip from './PerformanceTrendStrip';
import RiskAdjustedPerformancePanel from './RiskAdjustedPerformancePanel';
import DrawdownPathPanel from './DrawdownPathPanel';

const PERIODS = ['7d', '14d', '1m', '3m', '6m', '1y'];

function fmt(value, digits = 2, suffix = '') {
  const number = Number(value);
  return Number.isFinite(number) ? `${number.toFixed(digits)}${suffix}` : '—';
}

function signedFmt(value, digits = 0, suffix = '') {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return `${number > 0 ? '+' : ''}${number.toFixed(digits)}${suffix}`;
}

function signedTone(value, inverse = false) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return 'neutral';
  const positive = inverse ? number < 0 : number > 0;
  return positive ? 'positive' : 'negative';
}

function freshnessText(quality) {
  if (!quality || quality.age_days == null) return '无法判断净值新鲜度';
  if (quality.age_days === 0) return '净值已更新至今天';
  return `距今天 ${quality.age_days} 天`;
}

function StabilityDiagnosis({ navPerf }) {
  const risk = navPerf?.periods?.['3m']?.risk || navPerf?.periods?.[navPerf?.default_period]?.risk;
  if (!risk) return { label: '待确认', tone: 'unknown', text: '历史净值不足，暂不能评价稳定性。' };
  const drawdown = Number(risk.max_drawdown_bp);
  const volatility = Number(risk.volatility_bp);
  const winRate = Number(risk.win_rate_pct);
  const warn = Number(navPerf?.thresholds?.warn_bp || 50);
  if (drawdown >= warn || winRate < 45) {
    return { label: '需关注', tone: 'warn', text: `回撤或正收益日占比偏弱，当前更应关注净值波动是否继续扩大。` };
  }
  if (volatility <= 8 && winRate >= 55) {
    return { label: '较稳定', tone: 'positive', text: '日波动较低且正收益日占比较高，净值兑现相对连续。' };
  }
  return { label: '一般', tone: 'neutral', text: '整体波动尚可，但仍需结合回撤修复和后续净值连续性观察。' };
}

function PerformanceHero({ result }) {
  const navPerf = result.nav_performance || {};
  const quality = navPerf.data_quality || {};
  const period = navPerf.periods?.['3m'] || navPerf.periods?.[navPerf.default_period] || {};
  const risk = period.risk || {};
  const stability = StabilityDiagnosis({ navPerf });
  const periodLabel = period.label || '当前区间';
  return (
    <section className="performance-hero">
      <div className="performance-hero-copy">
        <span className="performance-eyebrow">PERFORMANCE DIAGNOSIS · 表现诊断</span>
        <div className="performance-title-row">
          <h2>{stability.label}</h2>
          <span className={`performance-status tone-${stability.tone}`}>净值稳定性</span>
        </div>
        <p>{stability.text}</p>
        <div className="performance-hero-meta">
          <span>{navPerf.mode_label || '净值产品'}</span>
          <span>{quality.points || navPerf.total_points || 0} 个数据点</span>
          <span>{quality.oldest_date || '—'} 至 {quality.latest_date || navPerf.latest?.date || '—'}</span>
        </div>
      </div>
      <div className="performance-hero-kpis">
        <div><span>{periodLabel}收益</span><strong className={`tone-${signedTone(period.period_return_pct)}`}>{fmt(period.period_return_pct, 2, '%')}</strong><small>按净值序列计算</small></div>
        <div><span>{periodLabel}最大回撤</span><strong>{fmt(risk.max_drawdown_bp, 1, 'bp')}</strong><small>{risk.max_drawdown_recovered ? '已修复' : '尚未完全修复'}</small></div>
        <div><span>{periodLabel}日波动</span><strong>{fmt(risk.volatility_bp, 2, 'bp')}</strong><small>下行波动 {fmt(risk.downside_volatility_bp, 2, 'bp')}</small></div>
        <div className={`freshness-${quality.freshness_level || 'unknown'}`}><span>数据状态</span><strong>{quality.freshness_label || '待确认'}</strong><small>{freshnessText(quality)}</small></div>
      </div>
    </section>
  );
}

function MultiPeriodMatrix({ navPerf }) {
  const rows = PERIODS.map((key) => navPerf?.periods?.[key]).filter(Boolean);
  if (!rows.length) return <p className="decision-muted">暂无多周期表现数据。</p>;
  return (
    <div className="performance-matrix-wrap">
      <table className="performance-matrix">
        <thead><tr><th>观察区间</th><th>区间收益</th><th>最大回撤</th><th>当前回撤</th><th>日波动</th><th>正收益日</th><th>最长连跌</th><th>样本</th></tr></thead>
        <tbody>
          {rows.map((period) => {
            const risk = period.risk || {};
            return (
              <tr key={period.key}>
                <td><strong>{period.label}</strong><small>{period.calendar_days || 0}自然日</small></td>
                <td className={`tone-${signedTone(period.period_return_pct)}`}>{fmt(period.period_return_pct, 2, '%')}</td>
                <td className={risk.alert_level === 'danger' ? 'tone-negative' : risk.alert_level === 'warn' ? 'tone-warn' : ''}>{fmt(risk.max_drawdown_bp, 1, 'bp')}</td>
                <td>{fmt(risk.current_drawdown_bp, 1, 'bp')}</td>
                <td>{fmt(risk.volatility_bp, 2, 'bp')}</td>
                <td>{fmt(risk.win_rate_pct, 1, '%')}</td>
                <td>{risk.max_consecutive_down_days != null ? `${risk.max_consecutive_down_days}天` : '—'}</td>
                <td>{period.days_used || 0}日</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RelativeComparison({ result }) {
  const benchmark = result.nav_performance?.returns?.vs_benchmark || {};
  return (
    <div className="performance-relative-grid">
      <article className="relative-card">
        <div className="relative-card-head"><span>相对业绩基准</span><em>{benchmark.window_label || '近20日'}</em></div>
        <strong className={`tone-${signedTone(benchmark.vs_bp)}`}>{benchmark.available ? (benchmark.vs_text || fmt(benchmark.vs_bp, 1, 'bp')) : '暂不可比'}</strong>
        <p>{benchmark.available ? `产品 ${benchmark.product_cum_text || '—'} / 合成基准 ${benchmark.synthetic_cum_text || '—'}` : '业绩基准历史行情或匹配覆盖不足'}</p>
        <small>这部分衡量是否跑赢合同约定基准，不等同于绝对收益高低。</small>
      </article>
      <article className="relative-card">
        <div className="relative-card-head"><span>回撤修复能力</span><em>{result.nav_performance?.recovery_capability?.window_label || '近1年'}</em></div>
        <strong>{result.nav_performance?.recovery_capability?.strength_label || '待确认'}</strong>
        <p>{result.nav_performance?.recovery_capability?.text || '历史回撤样本不足，暂不能评价修复速度。'}</p>
        <small>修复快不代表不会发生更大回撤，需与最大回撤一起看。</small>
      </article>
    </div>
  );
}

function StrictPeerComparison({ comparison }) {
  if (!comparison) return <p className="decision-muted">严格同类样本尚未生成。</p>;
  const available = (comparison.metrics || []).filter((metric) => metric.available);
  return (
    <div className="strict-peer-panel">
      <div className="strict-peer-boundary">
        <div><span>当前采用边界</span><strong>{comparison.selected_label || '样本不足'}</strong><small>{comparison.base_label} · {comparison.selected_sample_size || 0}个产品</small></div>
        <p>{comparison.summary}</p>
      </div>
      <div className="strict-peer-funnel">
        {(comparison.stages || []).map((stage, index) => (
          <div className={stage.selected ? 'is-selected' : ''} key={stage.key}>
            <span>{String(index + 1).padStart(2, '0')}</span>
            <p>{stage.label}</p>
            <strong>{stage.sample_size}</strong>
          </div>
        ))}
      </div>
      {available.length ? (
        <div className="strict-peer-table-wrap">
          <table className="strict-peer-table">
            <thead><tr><th>可比指标</th><th>本产品</th><th>严格同类中位数</th><th>中位数差</th><th>排名</th><th>分位</th><th>有效样本</th></tr></thead>
            <tbody>{available.map((metric) => (
              <tr key={metric.key}>
                <td>{metric.label}</td>
                <td>{fmt(metric.value, 2, metric.suffix)}</td>
                <td>{fmt(metric.median_value, 2, metric.suffix)}</td>
                <td className={`tone-${signedTone(metric.gap_vs_median)}`}>{fmt(metric.gap_vs_median, 2, metric.suffix)}</td>
                <td>{metric.rank_from_top}/{metric.sample_size}</td>
                <td>{fmt(metric.percentile, 0, '%')}</td>
                <td>{metric.sample_size}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : <p className="decision-muted">当前边界下有效收益样本少于3个，不强行给出排名。</p>}
      {available.some((metric) => metric.trend?.available) ? (
        <div className="peer-trend-block">
          <div className="peer-trend-title">
            <div><span>STRICT PEER TREND</span><strong>同类分位趋势</strong></div>
            <p>观察最近7、14、30个历史排名点；分位越高，表示在当前严格同类中相对靠前。</p>
          </div>
          <div className="peer-trend-grid">
            {available.map((metric) => (
              <article key={`trend-${metric.key}`}>
                <div className="peer-trend-card-head">
                  <div><span>{metric.label}</span><strong>{fmt(metric.trend?.latest_percentile, 0, '%')}</strong></div>
                  <small>{metric.trend?.history_points || 0}个历史排名点</small>
                </div>
                <div className="peer-trend-windows">
                  {(metric.trend?.windows || []).map((window) => (
                    <div className={window.available ? `trend-${window.verdict?.key || 'stable'}` : 'trend-unavailable'} key={window.key}>
                      <span>{window.label}</span>
                      {window.available ? (
                        <>
                          <strong>{fmt(window.start_avg_percentile, 0, '%')} → {fmt(window.end_avg_percentile, 0, '%')}</strong>
                          <em>{signedFmt(window.delta_percentile, 0, 'pct')} · {window.verdict?.label}</em>
                        </>
                      ) : <strong>样本不足（{window.sample_size || 0}）</strong>}
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
          <p className="peer-trend-note">趋势使用期初3个与期末3个排名点的平均分位，变化达到15个百分点才标记改善或走弱；它用于识别持续性，不单独触发买卖。</p>
        </div>
      ) : null}
      <p className="strict-peer-caveat">{comparison.caveat}</p>
    </div>
  );
}

function InterpretationStrip({ navPerf }) {
  const risk = navPerf?.periods?.['3m']?.risk || {};
  const items = [
    { label: '收益连续性', value: fmt(risk.win_rate_pct, 1, '%'), text: `近3月正收益日占比；涨/跌 ${risk.pos_days ?? '—'}/${risk.neg_days ?? '—'} 天` },
    { label: '下行风险', value: fmt(risk.downside_volatility_bp, 2, 'bp'), text: '仅统计负收益日的波动，越低通常越稳' },
    { label: '尾部波动', value: `${fmt(risk.max_single_day_up_bp, 1, 'bp')} / ${fmt(risk.max_single_day_down_bp, 1, 'bp')}`, text: '区间最大单日上涨 / 下跌' },
    { label: '当前水下幅度', value: fmt(risk.current_drawdown_bp, 1, 'bp'), text: risk.current_drawdown_peak_date ? `相对 ${risk.current_drawdown_peak_date} 区间高点` : '相对区间高点' }
  ];
  return <div className="performance-interpret-grid">{items.map((item) => <div key={item.label}><span>{item.label}</span><strong>{item.value}</strong><small>{item.text}</small></div>)}</div>;
}

function buildMatrixGuide(navPerf) {
  const periods = PERIODS.map((key) => navPerf?.periods?.[key]).filter(Boolean);
  const validReturns = periods.map((item) => Number(item.period_return_pct)).filter(Number.isFinite);
  const allPositive = validReturns.length > 0 && validReturns.every((value) => value >= 0);
  const allNegative = validReturns.length > 0 && validReturns.every((value) => value < 0);
  const focus = navPerf?.periods?.['3m'] || navPerf?.periods?.[navPerf?.default_period];
  const risk = focus?.risk || {};
  const warn = Number(navPerf?.thresholds?.warn_bp);
  const drawdownSafe = Number.isFinite(warn) && Number(risk.max_drawdown_bp) < warn;
  const direction = allPositive ? '各有效窗口收益均为正' : allNegative ? '各有效窗口收益均为负' : '不同窗口收益出现分化';
  const drawdown = Number.isFinite(Number(risk.max_drawdown_bp))
    ? `${focus.label}最大回撤 ${fmt(risk.max_drawdown_bp, 1, 'bp')}${drawdownSafe ? '，低于当前预警线' : '，需要重点关注'}`
    : '回撤样本不足';
  return {
    conclusion: `${direction}；${drawdown}；当前回撤 ${fmt(risk.current_drawdown_bp, 1, 'bp')}。`,
    use: '准备买入时重点看7日、14日和1月；评估持有质量时重点看3月和6月。不同期限的累计收益不能直接比大小判断“加速或减速”，要同时结合样本长度、回撤和波动。',
    fixed: true
  };
}

function buildRelativeGuide(result) {
  const peer = result.strict_peer_comparison || {};
  const benchmark = result.nav_performance?.returns?.vs_benchmark || {};
  const recovery = result.nav_performance?.recovery_capability || {};
  const peerText = peer.available
    ? `严格同类已选用“${peer.selected_label}”，共${peer.selected_sample_size}个产品`
    : '严格同类样本不足';
  const benchmarkText = benchmark.available
    ? `${benchmark.window_label || '当前窗口'}相对合成基准 ${benchmark.vs_text || fmt(benchmark.vs_bp, 1, 'bp')}`
    : '暂不能与业绩基准可靠比较';
  return {
    conclusion: `${peerText}；${benchmarkText}；回撤修复能力${recovery.strength_label || '待确认'}。`,
    use: '绝对收益为正并不等于表现优秀；同系列排名回答“相似产品中是否更好”，基准比较回答“是否兑现合同目标”。两者同时偏弱，才是减仓或寻找替代品的重要信号。',
    fixed: true
  };
}

function buildStabilityGuide(navPerf) {
  const risk = navPerf?.periods?.['3m']?.risk || {};
  return {
    conclusion: `近3月正收益日占比 ${fmt(risk.win_rate_pct, 1, '%')}，下行波动 ${fmt(risk.downside_volatility_bp, 2, 'bp')}，最长连续下跌 ${risk.max_consecutive_down_days ?? '—'} 天。`,
    use: '稳健产品不能只看最终收益。正收益日占比越高、下行波动越小、连续下跌越短，持有体验通常越稳定；但异常的超高正收益日占比也要结合净值是否长期不动、是否存在估值滞后。',
    fixed: true
  };
}

function SectionGuide({ guide }) {
  if (!guide) return null;
  return (
    <div className="performance-reading-guide">
      <div><span>{guide.fixed ? '系统固定结论' : '阅读方式'}</span><p>{guide.conclusion}</p></div>
      <div><span>你应该怎么用</span><p>{guide.use}</p></div>
    </div>
  );
}

function PerformanceSection({ title, subtitle, guide, children, className = '' }) {
  return (
    <section className={`decision-card performance-section ${className}`}>
      <div className="performance-section-head"><div><span>PRODUCT PERFORMANCE</span><h3>{title}</h3></div><p>{subtitle}</p></div>
      <SectionGuide guide={guide} />
      {children}
    </section>
  );
}

export default function ProductPerformanceTab({ result }) {
  const navPerf = result?.nav_performance;
  if (!navPerf?.available) {
    return <div className="decision-card product-tab-panel"><p className="decision-muted">暂无净值历史，暂不能生成产品表现分析。</p></div>;
  }
  return (
    <div className="product-performance-tab product-tab-panel" role="tabpanel">
      <PerformanceHero result={result} />
      <PerformanceSection title="多周期表现矩阵" subtitle="同口径横向比较收益、回撤、波动与连续性；窗口不足时以实际样本显示。" guide={buildMatrixGuide(navPerf)}>
        <MultiPeriodMatrix navPerf={navPerf} />
      </PerformanceSection>
      <PerformanceSection title="近期表现状态" subtitle="前后相同长度窗口对比，不再用7日、14日、1月累计收益互相判断趋势。" guide={{ fixed: true, conclusion: result.performance_trend?.summary || '历史净值样本不足，暂不能生成前后窗口趋势。', use: '改善可作为持有或后续加仓的辅助依据；走弱时先暂停加仓，再看同类排名、回撤和异常日是否同步恶化。该状态暂不改变综合分。' }}>
        <PerformanceTrendStrip trend={result.performance_trend} />
      </PerformanceSection>
      <PerformanceSection title="风险调整后表现" subtitle="用7、14、30个净值变动点观察获得收益时承担了多少回撤、波动和下行风险。" guide={{ fixed: true, conclusion: result.risk_adjusted_performance?.summary || '净值变动样本不足，暂不能生成风险效率指标。', use: '不能只看收益/回撤比一个数字。准备买入重点看14个变动点，评估持有质量重点看30个变动点；加仓还要确认7个变动点没有明显恶化，并结合上方近期状态和异常日一起使用。' }}>
        <RiskAdjustedPerformancePanel profile={result.risk_adjusted_performance} />
      </PerformanceSection>
      <PerformanceSection title="回撤路径与修复" subtitle="主结论使用全部已有单位净值记录识别前高、谷底和修复过程；7、14、30点仅作为近期切片。" guide={{ fixed: true, conclusion: result.drawdown_path_analysis?.summary || '单位净值样本不足，暂不能生成回撤路径。', use: '回撤扩大时先暂停加仓；修复中要看是否真正回到历史前高；已经修复也不能单独作为买入理由。减仓需要回撤路径、近期状态、同类排名和市场因素共同确认。' }}>
        <DrawdownPathPanel analysis={result.drawdown_path_analysis} />
      </PerformanceSection>
      <PerformanceSection title="严格同类与相对表现" subtitle="先按系列、风险等级、持有期限和确认时滞缩小样本，再比较收益与业绩基准。" guide={buildRelativeGuide(result)}>
        <StrictPeerComparison comparison={result.strict_peer_comparison} />
        <RelativeComparison result={result} />
      </PerformanceSection>
      <PerformanceSection title="稳定性拆解" subtitle="这些指标只做事实展示和解释，暂不改变综合评分阈值。" guide={buildStabilityGuide(navPerf)}>
        <InterpretationStrip navPerf={navPerf} />
        <NavAnomalyPanel review={navPerf.anomaly_review} />
      </PerformanceSection>
      <PerformanceSection title="净值与收益走势" subtitle="切换观察周期，四张图按同一日期联动；下方保留完整收益和风险指标。" guide={{ fixed: false, conclusion: '走势图没有脱离观察周期后的唯一结论，它用于确认收益是否平滑、回撤发生在哪一天，以及万份收益和年化指标是否同步异常。', use: '先选择与你计划持有期接近的窗口，再把鼠标移到回撤点；如果净值、万份收益和年化同时明显下坠，通常是实际波动，如果只有单字段异常则优先检查数据质量。' }} className="performance-chart-section">
        <NavPerformancePanel key={result.product_id} navPerf={navPerf} />
      </PerformanceSection>
      <PerformanceSection title="产品基础信息" subtitle="用于确认产品类型、风险等级、期限和业绩目标，避免跨口径比较。" guide={{ fixed: false, conclusion: '基础信息本身不产生买入或卖出结论，它定义了比较边界和你需要承担的合同约束。', use: '重点核对风险等级、最短持有期、产品系列和业绩基准。只有这些条件相近的产品，收益、回撤和排名才适合直接比较。' }} className="performance-fundamentals-section">
        <ProductFundamentalsCard profile={result.product_profile} />
      </PerformanceSection>
    </div>
  );
}
